import { useEffect, useRef, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAddressAutocomplete } from '../../hooks/useMaps';
import { PLACE_TYPES_REGIONS } from '../../services/maps-api';
import { isPostalCodeInput } from '../../utils/address';
import type { AddressSuggestion } from '../../types/address';

/**
 * The "where are you" text input, shared by every locale-entry surface:
 * the TopBar locale switcher (`LocaleSelectorModal`), the home-feed prompt
 * (`InlineLocationPrompt`), the order gate (`AddressCaptureModal`), and the
 * profile-address minimum path (`ProfileLocationModal`).
 *
 * Accepts a city (or neighborhood / county / state) OR a ZIP in one field:
 *  - A ZIP is submitted as typed — no Places call, no dropdown. The resolve
 *    path downstream is byte-identical to what it was before this component
 *    existed.
 *  - Anything else is debounced into region autocomplete
 *    (`types=(regions)`, US-restricted server-side) and rendered as a
 *    dropdown. Picking a suggestion submits its full description
 *    ("Denver, CO, USA"), which is unambiguous to geocode — the reason we
 *    never submit the user's raw half-typed text on a pick.
 *
 * This component owns the input UI and the suggestion list only. What a
 * submitted string *means* is the caller's business: `useLocaleCapture`
 * turns it into a browse locale, `useAddressCapture` turns it into a saved
 * address. Both take the text through `useResolvePlace`.
 */
interface LocaleSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Fired on form submit and on suggestion pick. Receives the exact text to
   * resolve rather than reading `value` — on a pick, the parent's state has
   * not flushed yet, so `value` is still the half-typed input.
   */
  onSubmit: (text: string) => void;
  submitting?: boolean;
  error?: string | null;
  label: string;
  placeholder: string;
  ctaLabel: string;
  /** Unique DOM id, for the <label for> association. */
  inputId: string;
  /** `md` matches the modal/prompt controls, `sm` the tighter profile modal. */
  size?: 'md' | 'sm';
  testIds?: { input?: string; submit?: string; error?: string };
  className?: string;
}

/** Below this, autocomplete would just return noise. Matches LocationPicker. */
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

export const LocaleSearchInput: React.FC<LocaleSearchInputProps> = ({
  value,
  onChange,
  onSubmit,
  submitting = false,
  error,
  label,
  placeholder,
  ctaLabel,
  inputId,
  size = 'md',
  testIds,
  className,
}) => {
  const { t } = useTranslation('location');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const autocompleteMutation = useAddressAutocomplete({ types: PLACE_TYPES_REGIONS });

  const dropdownOpen = suggestions.length > 0 || noResults;

  // Dismiss the dropdown on an outside click (same pattern as LocationPicker).
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleMouseDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setSuggestions([]);
        setNoResults(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dropdownOpen]);

  // Cancel a pending lookup on unmount so a closing modal doesn't fire one.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  function closeDropdown() {
    setSuggestions([]);
    setNoResults(false);
  }

  function handleChange(text: string) {
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // A ZIP is already an exact answer — skip autocomplete entirely rather
    // than spend a Places request on it (and keep the ZIP path free of a
    // dropdown that would cover the submit button).
    if (text.trim().length < MIN_QUERY_LENGTH || isPostalCodeInput(text)) {
      closeDropdown();
      return;
    }

    debounceRef.current = setTimeout(() => {
      autocompleteMutation.mutate(
        { searchText: text, sessionToken },
        {
          onSuccess: (result) => {
            setSessionToken(result.sessionToken);
            setSuggestions(result.suggestions);
            setNoResults(result.suggestions.length === 0);
          },
          // Autocomplete is an assist, not the submit path — a failed lookup
          // silently closes the dropdown and leaves the typed text
          // submittable rather than surfacing an error the user can't act on.
          onError: () => closeDropdown(),
        },
      );
    }, DEBOUNCE_MS);
  }

  function handleSelect(suggestion: AddressSuggestion) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    closeDropdown();
    onChange(suggestion.address);
    onSubmit(suggestion.address);
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    closeDropdown();
    onSubmit(value.trim());
  }

  const inputClass =
    size === 'sm'
      ? 'w-0 min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'
      : 'w-0 min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary';
  const buttonClass =
    size === 'sm'
      ? 'flex shrink-0 items-center justify-center rounded-lg btn-outline border-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary active:opacity-80 disabled:opacity-50'
      : 'flex shrink-0 items-center justify-center gap-2 rounded-xl btn-outline border-brand-primary px-4 text-sm font-semibold text-brand-primary active:opacity-80 disabled:opacity-50';

  return (
    <form onSubmit={handleFormSubmit} className={className ?? (size === 'sm' ? 'flex flex-col gap-1' : 'space-y-2')}>
      <label className={size === 'sm' ? 'text-sm font-medium text-foreground' : 'block text-sm font-medium'} htmlFor={inputId}>
        {label}
      </label>
      {/* `relative` anchors the absolutely-positioned dropdown to the whole
          row (input + button) so it spans the full control width. */}
      <div ref={wrapperRef} className="relative">
        <div className="flex gap-2">
          <input
            id={inputId}
            data-testid={testIds?.input}
            type="text"
            // No `inputMode="numeric"` any more: the field takes city names
            // now, and a numeric keypad would make them untypeable on mobile.
            autoComplete="off"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeDropdown();
            }}
            placeholder={placeholder}
            className={inputClass}
          />
          <button
            type="submit"
            data-testid={testIds?.submit}
            disabled={submitting || !value.trim()}
            className={buttonClass}
          >
            {submitting ? <IonSpinner name="crescent" className="h-5 w-5" /> : ctaLabel}
          </button>
        </div>
        {dropdownOpen && (
          <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-border bg-background shadow-lg">
            {suggestions.map((s) => (
              <button
                key={s.placeId}
                type="button"
                onClick={() => handleSelect(s)}
                className="w-full border-b border-border px-3 py-2 text-left text-sm text-foreground last:border-b-0 hover:bg-surface-secondary"
              >
                {s.address}
              </button>
            ))}
            {noResults && (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('search.noResults')}</p>
            )}
          </div>
        )}
      </div>
      {error && (
        <p
          data-testid={testIds?.error}
          className={size === 'sm' ? 'text-xs text-brand-danger' : 'text-sm text-brand-danger'}
        >
          {error}
        </p>
      )}
    </form>
  );
};
